import React, { useCallback, useMemo, useState } from 'react';
import {
  acceptDonorSession,
  completeDonorCapture,
  consentDonorSession,
  declineDonorSession,
  fetchDonorCaptureProgress,
  GuestCaptureProgressResponse,
  requestDonorSessionResumeLink,
  revokeDonorSessionShare,
  revokeDonorSessionVoice,
} from '../../api/donorSessionApi';
import { ApiError, toApiError } from '../../api/http';
import type { DonorInvitationPresentation } from '../../api/voiceInvitationsApi';
import {
  createDonorSessionCaptureClient,
  validateGuestCaptureProgress,
} from '../../audio/donorSessionCaptureClient';
import GuidedCapture from '../../components/VoiceCapture/GuidedCapture';
import { EmptyState, FormField, Panel, PillButton, Sheet, StateMessage } from '../../components/ui';
import DonorConsent from './DonorConsent';
import DonorGiftFlow from './DonorGiftFlow';
import ManagementEmailButton from '../DonorManagement/ManagementEmailButton';
import { approvedDonorPolicies } from './donorConsentPolicy';

interface DonorCaptureFlowProps {
  presentation: DonorInvitationPresentation;
  onSessionUnavailable: (error: ApiError) => void;
  onDeclined: () => void;
}

function lifecycleError(error: ApiError): boolean {
  return error.status === 401 || error.status === 403 || error.status === 410;
}

/** "Maya", "Maya and Leo", or "Maya, Leo and Sam". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const DonorCaptureFlow: React.FC<DonorCaptureFlowProps> = ({
  presentation,
  onSessionUnavailable,
  onDeclined,
}) => {
  const [progress, setProgress] = useState<GuestCaptureProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [checkpointAcknowledged, setCheckpointAcknowledged] = useState(false);
  const [resumeRequested, setResumeRequested] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [donorDisplayName, setDonorDisplayName] = useState('');
  const [relationshipLabel, setRelationshipLabel] = useState('');
  const [voiceLabel, setVoiceLabel] = useState('My voice');
  const [detailsValidation, setDetailsValidation] = useState(false);
  const [revocationStatus, setRevocationStatus] = useState<string | null>(null);
  const [pendingRevocation, setPendingRevocation] = useState<'share' | 'voice' | null>(null);
  const completionAttemptRef = React.useRef<string | null>(null);

  const policiesApproved = useMemo(
    () => approvedDonorPolicies(presentation.policies) !== null,
    [presentation.policies],
  );

  const refreshProgress = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const next = validateGuestCaptureProgress(await fetchDonorCaptureProgress());
      setProgress(next);
    } catch (reason) {
      const apiError = toApiError(reason);
      if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [onSessionUnavailable]);

  React.useEffect(() => {
    if (!policiesApproved) {
      setLoading(false);
      return;
    }
    void refreshProgress();
  }, [policiesApproved, refreshProgress]);

  const captureClient = useMemo(() => {
    if (!progress) return null;
    return createDonorSessionCaptureClient({
      initialProgress: progress,
      onLifecycleError: (apiError) => {
        if (apiError.status === 409) void refreshProgress();
        else onSessionUnavailable(apiError);
      },
    });
  }, [onSessionUnavailable, progress, refreshProgress]);

  const decline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await declineDonorSession();
      onDeclined();
    } catch (reason) {
      const apiError = toApiError(reason);
      if (lifecycleError(apiError) || apiError.code === 'conflict') {
        onSessionUnavailable(apiError);
      } else setError(true);
    } finally {
      setBusy(false);
    }
  };

  const requestResume = async () => {
    if (busy || resumeRequested) return;
    setBusy(true);
    try {
      await requestDonorSessionResumeLink();
      setResumeRequested(true);
    } catch (reason) {
      const apiError = toApiError(reason);
      if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setBusy(false);
    }
  };

  const submitDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    const donor = donorDisplayName.trim().replace(/\s+/g, ' ');
    const relationship = relationshipLabel.trim().replace(/\s+/g, ' ');
    const voice = voiceLabel.trim().replace(/\s+/g, ' ');
    if (
      !donor || donor.length > 200
      || relationship.length > 100
      || !voice || voice.length > 200
    ) {
      setDetailsValidation(true);
      return;
    }
    setDetailsValidation(false);
    setBusy(true);
    setError(false);
    try {
      const accepted = await acceptDonorSession({
        donorDisplayName: donor,
        relationshipLabel: relationship || null,
        voiceLabel: voice,
      });
      setVoiceLabel(accepted.voice_label);
      await refreshProgress();
    } catch (reason) {
      const apiError = toApiError(reason);
      if (apiError.code === 'conflict') await refreshProgress();
      else if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setBusy(false);
    }
  };

  const submitConsent = async () => {
    setBusy(true);
    setError(false);
    try {
      const response = await consentDonorSession({
        voice_model_creation: 'granted',
        household_voice_share: 'granted',
        terms: 'acknowledged',
        privacy: 'acknowledged',
      });
      if (response.state !== 'consented' || response.consent_event_count !== 4) {
        throw new Error('Consent response was incomplete.');
      }
      await refreshProgress();
    } catch (reason) {
      const apiError = toApiError(reason);
      if (apiError.code === 'conflict') await refreshProgress();
      else if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (kind: 'share' | 'voice') => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      if (kind === 'share') await revokeDonorSessionShare();
      else await revokeDonorSessionVoice();
      setRevocationStatus(kind === 'share'
        ? 'Future household sharing is blocked.'
        : 'Future voice-model use is blocked.');
      await refreshProgress();
    } catch (reason) {
      const apiError = toApiError(reason);
      if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setPendingRevocation(null);
      setBusy(false);
    }
  };

  const finalizeCapture = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const completed = await completeDonorCapture();
      if (
        completed.state !== 'completed'
        || completed.durable_capture_complete !== true
      ) {
        throw new Error('Capture completion response was incomplete.');
      }
      await refreshProgress();
    } catch (reason) {
      const apiError = toApiError(reason);
      if (lifecycleError(apiError)) onSessionUnavailable(apiError);
      else setError(true);
    } finally {
      setBusy(false);
    }
  }, [busy, onSessionUnavailable, refreshProgress]);

  React.useEffect(() => {
    if (
      progress?.stage !== 'complete'
      || progress.durable_capture_complete !== true
      || completionAttemptRef.current === progress.draft_scope_key
    ) return;
    completionAttemptRef.current = progress.draft_scope_key;
    void finalizeCapture();
  }, [finalizeCapture, progress]);

  /** Decline confirmation. The sentence differs by stage; the buttons do not. */
  const declineSheet = (body: string) => (
    <Sheet open={confirmDecline} title="Decline this invitation?" onClose={() => setConfirmDecline(false)}>
      <p>{body}</p>
      <div className="donor-actions donor-actions--sheet">
        <PillButton variant="secondary" onClick={() => setConfirmDecline(false)}>Keep invitation</PillButton>
        <PillButton variant="destructive" busy={busy} busyLabel="Declining…" onClick={() => void decline()}>Yes, decline</PillButton>
      </div>
    </Sheet>
  );

  const revocationControls = (
    <>
      <Panel purpose="settings" labelledBy="donor-revocation-title" className="donor-revocation">
        <h2 id="donor-revocation-title">Change a voice permission</h2>
        <ManagementEmailButton />
        <p>These actions block future use. They do not erase the record of choices you already made.</p>
        <div className="donor-actions">
          <PillButton variant="destructive" disabled={busy} onClick={() => setPendingRevocation('share')}>
            Stop household sharing
          </PillButton>
          <PillButton variant="destructive" disabled={busy} onClick={() => setPendingRevocation('voice')}>
            Stop voice-model use
          </PillButton>
        </div>
        {revocationStatus && <p className="donor-note" role="status">{revocationStatus}</p>}
      </Panel>
      <Sheet open={pendingRevocation !== null} title="Confirm this permission change" onClose={() => setPendingRevocation(null)}>
        <p>{pendingRevocation === 'share'
          ? 'This immediately blocks future household sharing.'
          : 'This immediately blocks future creation or use of the voice model.'}</p>
        <div className="donor-actions donor-actions--sheet">
          <PillButton variant="secondary" disabled={busy} onClick={() => setPendingRevocation(null)}>Keep permission</PillButton>
          <PillButton
            variant="destructive"
            busy={busy}
            busyLabel="Confirming…"
            onClick={() => { if (pendingRevocation) void revoke(pendingRevocation); }}
          >
            {pendingRevocation === 'share' ? 'Yes, stop household sharing' : 'Yes, stop voice-model use'}
          </PillButton>
        </div>
      </Sheet>
    </>
  );

  if (!policiesApproved) {
    return (
      <div className="donor-step" role="alert">
        <EmptyState
          headingLevel={1}
          title="This invitation needs an updated consent form"
          message="Recording has not started. Ask the person who invited you for a new link."
        />
      </div>
    );
  }

  if (loading && !progress) {
    return <StateMessage kind="loading" title="Checking your saved progress…" shape="text" className="donor-step" />;
  }

  if (error && !progress) {
    return (
      <div className="donor-step" role="alert">
        <h1>We couldn’t confirm your saved progress</h1>
        <p>Nothing was changed. Try again before recording.</p>
        <div className="donor-actions">
          <PillButton variant="primary" onClick={() => void refreshProgress()}>Try again</PillButton>
        </div>
      </div>
    );
  }

  if (!progress || !captureClient) return null;

  if (paused) {
    return (
      <div className="donor-step">
        <h1>Your saved recordings are here</h1>
        <p>You can continue in this browser while the session is active.</p>
        {error && <p className="donor-error" role="alert">That request didn’t work. Your saved recordings were not changed.</p>}
        {resumeRequested && <p className="donor-note" role="status">Request received. If a fresh link is available, it will be sent to the address already on the invitation.</p>}
        <div className="donor-actions">
          <PillButton variant="primary" onClick={() => setPaused(false)}>Continue recording</PillButton>
          <PillButton variant="secondary" disabled={busy || resumeRequested} onClick={() => void requestResume()}>
            {resumeRequested ? 'Request received' : 'Request a link to continue'}
          </PillButton>
          <PillButton variant="legal" onClick={() => setConfirmDecline(true)}>Decline invitation</PillButton>
        </div>
        {declineSheet('This closes the invitation and blocks future recording.')}
      </div>
    );
  }

  if (progress.stage === 'details') {
    return (
      <div className="donor-step" aria-busy={busy || undefined}>
        <h1>What should your family call this voice?</h1>
        <p>These labels help this household recognize your recordings. They do not create a Luma account.</p>
        <form className="donor-form" onSubmit={(event) => void submitDetails(event)} noValidate>
          <FormField id="donor-display-name" label="Your name" disabled={busy}>
            <input maxLength={200} autoComplete="name" value={donorDisplayName} onChange={(event) => setDonorDisplayName(event.target.value)} />
          </FormField>
          <FormField id="donor-relationship" label="Relationship to the listeners (optional)" disabled={busy}>
            <input maxLength={100} autoComplete="off" placeholder="e.g., Grandparent" value={relationshipLabel} onChange={(event) => setRelationshipLabel(event.target.value)} />
          </FormField>
          <FormField id="donor-voice-label" label="Voice name" disabled={busy}>
            <input maxLength={200} autoComplete="off" value={voiceLabel} onChange={(event) => setVoiceLabel(event.target.value)} />
          </FormField>
          {detailsValidation && <p className="donor-error" role="alert">Enter your name and a voice name to continue.</p>}
          {error && <p className="donor-error" role="alert">We couldn’t save these labels. Nothing was changed; try again.</p>}
          <div className="donor-actions">
            <PillButton variant="primary" type="submit" busy={busy} busyLabel="Saving…">Continue to consent</PillButton>
            <PillButton variant="legal" disabled={busy} onClick={() => setConfirmDecline(true)}>Decline invitation</PillButton>
          </div>
        </form>
        {declineSheet('No recording has started.')}
      </div>
    );
  }

  if (progress.stage === 'consent') {
    return (
      <>
        <DonorConsent
          policies={presentation.policies}
          busy={busy}
          error={error}
          onSubmit={() => void submitConsent()}
          onDecline={() => setConfirmDecline(true)}
        />
        {declineSheet('This closes the invitation. Recording has not started.')}
      </>
    );
  }

  if (progress.stage === 'voice_samples') {
    return (
      <GuidedCapture
        client={captureClient}
        target={{ kind: 'voice' }}
        voiceLabel={voiceLabel || 'My voice'}
        speakerDisplayLabel={donorDisplayName || 'someone who loves you'}
        completionKind="capture"
        collectVoiceLabel={false}
        onComplete={() => void refreshProgress()}
        onCancel={() => setPaused(true)}
      />
    );
  }

  const hasAnyListenerClip = progress.listeners.some(
    (listener) => listener.persisted_clip_keys.length > 0,
  );
  if (
    progress.stage === 'listener_clips'
    && !checkpointAcknowledged
    && !hasAnyListenerClip
  ) {
    return (
      <div className="donor-step" role="status">
        <h1>Your six recordings are saved</h1>
        <p>Next, record three short personal moments for each listener. Your saved voice passages will not be repeated.</p>
        <div className="donor-actions">
          <PillButton variant="primary" onClick={() => setCheckpointAcknowledged(true)}>
            Continue to personal messages
          </PillButton>
        </div>
      </div>
    );
  }

  if (progress.stage === 'listener_clips') {
    const listener = progress.listeners.find((candidate) => !candidate.complete);
    if (!listener) {
      return (
        <div className="donor-step" role="alert">
          <h1>We couldn’t confirm the next listener</h1>
          <p>Your saved recordings were not changed.</p>
          <div className="donor-actions">
            <PillButton variant="primary" onClick={() => void refreshProgress()}>Check again</PillButton>
          </div>
        </div>
      );
    }
    return (
      <GuidedCapture
        key={listener.invitation_listener_id}
        client={captureClient}
        target={{ kind: 'listener', listenerId: listener.invitation_listener_id }}
        voiceLabel={voiceLabel || 'My voice'}
        speakerDisplayLabel={donorDisplayName || 'someone who loves you'}
        listenerDisplayLabel={listener.display_name}
        completionKind="capture"
        onComplete={() => void refreshProgress()}
        onCancel={() => setPaused(true)}
      />
    );
  }

  const terminalCopy = {
    complete: ['Recordings complete', 'All voice passages and personal messages are saved. Voice building has not started yet.'],
    building: ['Your voice is being prepared', 'Your recordings are saved while the voice is prepared.'],
    ready: ['Your voice is ready', 'The household can use the voice only while the required permissions remain active.'],
    blocked: ['Future voice use is blocked', 'Your saved history remains recorded, but this invitation cannot continue.'],
  }[progress.stage];
  const listenerNames = presentation.listeners.map((listener) => listener.display_name);
  const showGift = progress.durable_capture_complete && ['building', 'ready'].includes(progress.stage);

  return (
    <div className="donor-step">
      {showGift && <DonorGiftFlow listenerNames={listenerNames} headingLevel={1} />}
      {showGift ? <h2>{terminalCopy[0]}</h2> : <h1>{terminalCopy[0]}</h1>}
      <p role="status">{terminalCopy[1]}</p>
      {progress.stage === 'complete' && busy && <p className="donor-note" role="status">Confirming your saved recordings…</p>}
      {error && (
        <p className="donor-error" role="alert">
          {progress.stage === 'complete'
            ? 'We couldn’t start voice preparation. Your recordings are still saved.'
            : 'That permission change could not be confirmed. Try again.'}
        </p>
      )}
      {progress.stage === 'complete' && error && (
        <div className="donor-actions">
          <PillButton variant="primary" disabled={busy} onClick={() => void finalizeCapture()}>
            Try making your voice again
          </PillButton>
        </div>
      )}
      {showGift && (
        <p className="donor-note donor-engagement">
          We can let you know when {listenerNames.length > 0 ? joinNames(listenerNames) : 'your listener'} hears a story in your voice.
        </p>
      )}
      {revocationControls}
    </div>
  );
};

export default DonorCaptureFlow;
